/**
 * Comminution — crush rigid sub-fracture pieces into powder.
 *
 * Pure bookkeeping: same pieces + energy → same pulverized volume.
 * Visible chunks are pieces at or above the profile size floor. Powder comes
 * first from in-core fines below `pulverizeSizeThreshold`, then from the
 * smallest leftovers scaled by coefficient × energy density, never dropping
 * visible-chunk volume below ~50% of the parent/carved volume.
 */

export interface ComminutionParams {
  /** 0 = splinters only (wood/glass). Brick ~0.35, concrete ~0.2, stone ~0.06. */
  coefficient: number;
  /** Pieces smaller than this inside the high-energy core become powder. */
  pulverizeSizeThreshold: number;
}

export interface ComminutionPiece {
  volume: number;
  /** Distance from the impact epicenter (same space as `coreRadius`). */
  dist: number;
}

export interface ComminutionInput {
  pieces: readonly ComminutionPiece[];
  params: ComminutionParams;
  /** J/m³ in the high-energy core (energy / core volume). */
  energyDensity: number;
  coreRadius: number;
  /** Profile visible-size floor — pieces below this are not "visible chunks". */
  sizeFloorVol: number;
  /** Minimum fraction of input volume that must remain visible chunks. */
  minChunkFraction?: number;
}

export interface ComminutionResult {
  keepIndices: number[];
  pulverizeIndices: number[];
  pulverizedVolume: number;
  chunkVolume: number;
}

/** Core is this fraction of the damage-event footprint radius. */
export const HIGH_ENERGY_CORE_FRACTION = 0.4;

/**
 * Energy density at which extraFraction equals the material coefficient.
 * Cannon probe (~1.9 kJ into a ~0.4 m³ core) sits well below this so default
 * brick shots convert leftover fines, not the visible rubble pile.
 */
export const COMMINUTION_REF_ENERGY_DENSITY = 15_000;

/** Floor so a default-power carve still reads as chunks, not a vanished hole. */
export const MIN_VISIBLE_CHUNK_FRACTION = 0.5;

export function extraPulverizeFraction(coefficient: number, energyDensity: number): number {
  if (!(coefficient > 0) || !(energyDensity > 0)) return 0;
  return Math.min(1, coefficient * (energyDensity / COMMINUTION_REF_ENERGY_DENSITY));
}

export function coreRadiusFromFootprint(
  footprint:
    | { kind: 'sphere'; radius: number }
    | { kind: 'point'; effectiveRadius: number },
): number {
  const r = footprint.kind === 'sphere' ? footprint.radius : footprint.effectiveRadius;
  if (!Number.isFinite(r) || r <= 0) return 0.05;
  return Math.max(0.05, r * HIGH_ENERGY_CORE_FRACTION);
}

export function energyDensityFromEvent(energy: number, coreRadius: number): number {
  const r = Number.isFinite(coreRadius) && coreRadius > 0 ? coreRadius : 0.05;
  const coreVol = (4 / 3) * Math.PI * r * r * r;
  const e = Number.isFinite(energy) && energy > 0 ? energy : 0;
  return e / Math.max(coreVol, 1e-6);
}

export function applyComminution(input: ComminutionInput): ComminutionResult {
  const pieces = input.pieces;
  const n = pieces.length;
  const totalVolume = pieces.reduce((sum, piece) => sum + Math.max(0, piece.volume), 0);
  const empty = (): ComminutionResult => ({
    keepIndices: pieces.map((_, i) => i),
    pulverizeIndices: [],
    pulverizedVolume: 0,
    chunkVolume: totalVolume,
  });

  if (n === 0 || totalVolume <= 0 || !(input.params.coefficient > 0)) return empty();

  const pulverize = new Array<boolean>(n).fill(false);
  const threshold = Math.max(0, input.params.pulverizeSizeThreshold);
  const coreRadius = Math.max(0, input.coreRadius);
  const sizeFloor = Math.max(0, input.sizeFloorVol);
  const minChunkFraction = input.minChunkFraction ?? MIN_VISIBLE_CHUNK_FRACTION;
  const minVisible = minChunkFraction * totalVolume;

  for (let i = 0; i < n; i += 1) {
    const piece = pieces[i];
    if (piece.volume < threshold && piece.dist <= coreRadius) pulverize[i] = true;
  }

  const remaining: number[] = [];
  for (let i = 0; i < n; i += 1) {
    if (!pulverize[i]) remaining.push(i);
  }
  remaining.sort((a, b) => {
    const aBelow = pieces[a].volume < sizeFloor ? 0 : 1;
    const bBelow = pieces[b].volume < sizeFloor ? 0 : 1;
    if (aBelow !== bBelow) return aBelow - bBelow;
    if (pieces[a].volume !== pieces[b].volume) return pieces[a].volume - pieces[b].volume;
    return a - b;
  });

  let remainingVol = 0;
  for (const i of remaining) remainingVol += pieces[i].volume;
  let extraBudget = extraPulverizeFraction(input.params.coefficient, input.energyDensity) * remainingVol;

  const visibleKept = (): number => {
    let vol = 0;
    for (let i = 0; i < n; i += 1) {
      if (!pulverize[i] && pieces[i].volume >= sizeFloor) vol += pieces[i].volume;
    }
    return vol;
  };

  for (const i of remaining) {
    if (extraBudget <= 1e-12) break;
    const vol = pieces[i].volume;
    if (vol >= sizeFloor && visibleKept() - vol < minVisible - 1e-12) continue;
    pulverize[i] = true;
    extraBudget -= vol;
  }

  const keepIndices: number[] = [];
  const pulverizeIndices: number[] = [];
  let pulverizedVolume = 0;
  let chunkVolume = 0;
  for (let i = 0; i < n; i += 1) {
    if (pulverize[i]) {
      pulverizeIndices.push(i);
      pulverizedVolume += pieces[i].volume;
    } else {
      keepIndices.push(i);
      chunkVolume += pieces[i].volume;
    }
  }
  return { keepIndices, pulverizeIndices, pulverizedVolume, chunkVolume };
}
